import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Globe2, Sparkles } from "lucide-react";
import Stat from "../Stat/Stat";
import './Hero2.css'

const Hero2 = () => {
  const navigate = useNavigate();

  return (
    <section
      aria-labelledby="hero-heading"
      className="relative min-h-screen overflow-hidden bg-[#f5f5f5] pt-[110px] md:pt-[130px]"
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,0,0,0.08),_transparent_30%),radial-gradient(circle_at_bottom_left,_rgba(0,0,0,0.06),_transparent_35%)]" />
        <div className="absolute top-20 left-[-120px] h-72 w-72 rounded-full bg-[#FF0000]/10 blur-3xl" />
        <div className="absolute bottom-10 right-[-100px] h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid min-h-[calc(100vh-110px)] items-center gap-10 md:grid-cols-2 lg:gap-14">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center md:text-left"
          >
            {/* Mobile Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="mb-4 flex justify-center md:hidden"
            >
              <img
                src="/logo/logo.png"
                alt="IES Gear Logo"
                className="w-20 drop-shadow-xl"
              />
            </motion.div>

            {/* Premium Badge */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.55 }}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-4 py-2 text-xs sm:text-sm font-medium text-black shadow-lg backdrop-blur-xl"
            >
              <Sparkles size={14} className="text-[#FF0000]" />
              Premium Custom Combat Sports Manufacturing
            </motion.div>

            {/* Heading */}
            <motion.h1
              id="hero-heading"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.7 }}
              className="text-4xl font-bold leading-tight tracking-tight text-black sm:text-5xl lg:text-7xl"
            >
              <span className="text-[#FF0000]">Gear</span> Up With{" "}
              <span className="font-orbitron">IES</span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.38, duration: 0.7 }}
              className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-700 sm:text-lg md:mx-0 md:text-xl"
            >
              Headquartered in Dubai,{" "}
              <span className="font-semibold text-black">
                IES <span className="font-orbitron text-[#FF0000]">Gear</span>
              </span>{" "}
              delivers premium boxing gloves, sportswear, and activewear built
              for elite performance, refined craftsmanship, and lasting
              durability.
            </motion.p>

            {/* Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48, duration: 0.7 }}
              className="mt-6 grid gap-3 sm:grid-cols-3"
            >
              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center justify-center md:justify-start">
                  <ShieldCheck size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Premium Build</p>
                <p className="text-xs text-gray-600">Crafted for durability</p>
              </div>

              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center justify-center md:justify-start">
                  <Globe2 size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Global Reach</p>
                <p className="text-xs text-gray-600">Trusted worldwide</p>
              </div>

              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center justify-center md:justify-start">
                  <Sparkles size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Luxury Finish</p>
                <p className="text-xs text-gray-600">Premium OEM quality</p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.58, duration: 0.7 }}
              className="mt-8 flex flex-col items-center gap-4 sm:flex-row md:items-center"
            >
              <button
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-7 py-4 text-sm font-semibold text-white shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:bg-[#111] sm:text-base"
                aria-label="Navigate to contact page"
              >
                Get Quote
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>

              <p className="text-sm font-semibold text-[#FF0000] sm:text-base">
                Gear Up for Excellence
              </p>
            </motion.div>

            {/* Stat Card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.68, duration: 0.7 }}
              className="mt-8 hidden md:block"
            >
              <div className="inline-flex rounded-3xl border border-black/10 bg-white/85 p-2 shadow-2xl backdrop-blur-xl">
                <div className="stats bg-transparent text-black">
                  <Stat
                    title="Satisfied Clients"
                    value="150+"
                    description="Across 20+ countries"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center"
          >
            {/* Floating Glow */}
            <div className="absolute h-[340px] w-[340px] rounded-full bg-[#FF0000]/10 blur-3xl md:h-[460px] md:w-[460px]" />

            {/* Image Card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="relative w-full max-w-[560px]"
            >
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-white/70 to-white/30 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl" />
              <div className="relative rounded-[2rem] border border-white/60 bg-white/70 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl">
                <img
                  src="/Boxing/Categories/1.png"
                  alt="Hero showcasing boxing gloves"
                  className="w-full rounded-2xl object-cover"
                />
              </div>

              {/* Floating Mini Card */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-4 left-4 rounded-2xl border border-black/10 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-xl md:-bottom-5 md:left-6"
              >
                <p className="text-xs font-medium text-gray-500">Built For</p>
                <p className="text-sm font-bold text-black md:text-base">
                  Performance & Precision
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero2;